import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApolloError, useMutation } from "@apollo/react-hooks";
import { DocumentNode } from "graphql";
import type { TFunction } from "i18next";
import { MY_TIMESHEET_GET_GROUPED_BY_USERS_DASHBOARD_MONTHLY_LIST } from "my-timesheet/graphql";
import { sumTimeStrings } from "my-timesheet/utils";
import { RoleFilterInput } from "oc/types";
import parsePipedName from "oc/utils/get-parse-pipedName";

//
// --- Types & Interfaces ---
//

export interface Week {
  weekIndex: number;
  duration: string;
}

export interface TableData {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: string;
  searchField?: string;
  searchText?: string;
}

export interface RawRow {
  contractId: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  weeks?: Week[];
  [key: string]: any;
}

export interface QueryResponse {
  page: number;
  pageSize: number;
  rows: RawRow[];
  total: number;
  totalPages: number;
}

interface MutationResponse {
  groupedByUsersDashboardMonthlyList: QueryResponse;
}

interface MutationVariables {
  page?: number;
  pageSize?: number;
  startTime: string;
  endTime: string;
  roleFilters?: RoleFilterInput;
  sortField?: string;
  sortDirection?: string;
  searchField?: string;
  searchText?: string;
  role?: string;
}

export interface UseParams {
  periodStart?: string;
  periodEnd?: string;
  period: string;
  tableData?: TableData;
  roleFilters?: RoleFilterInput;
  role?: string;
}

export interface ColumnDef {
  id: string;
  label: string;
  align: "left" | "center";
  width: string;
  type: "avatar" | "text" | "actionArrow";
  sort?: boolean;
  search?: boolean;
}

interface UseReturn {
  columnDefsMobile: { id: string; columns: ColumnDef[] };
  columnDefsDesktop: { id: string; columns: ColumnDef[] };
  data: any[];
  loading: boolean;
  error?: ApolloError;
  total: number;
  totalPages: number;
  page: number;
  pageSize: number;
  runQuery: () => Promise<any>;
  queryData: QueryResponse;
}

//
// --- Column Definitions Generators ---
//

export const generateColumnDefs = (
  weeks: Week[],
  t: TFunction,
): ColumnDef[] => {
  const baseItems: ColumnDef[] = [
    {
      id: "avatar",
      label: t("OC_IMAGE"),
      align: "center",
      width: "85px",
      type: "avatar",
    },
    {
      id: "employeeName",
      label: t("OC_NAME"),
      align: "left",
      width: "auto",
      type: "text",
      sort: true,
      search: true,
    },
  ];

  const weekItems: ColumnDef[] = weeks.map((week) => ({
    id: `${week.weekIndex - 1}`,
    label: `${week.weekIndex}. ${t("MY_TIMESHEET_WEEK")}`,
    align: "left",
    width: "94px",
    type: "text",
  }));

  const summaryItem: ColumnDef = {
    id: "sumDuration",
    label: t("OC_SUMMARY"),
    align: "left",
    width: "120px",
    type: "text",
    sort: true,
  };

  return [...baseItems, ...weekItems, summaryItem];
};

export const generateColumnDefsIsMobile = (
  weeks: Week[],
  t: TFunction,
): ColumnDef[] => {
  return [
    {
      id: "avatar",
      label: t("OC_IMAGE"),
      align: "center",
      width: "85px",
      type: "avatar",
    },
    {
      id: "employeeName",
      label: t("OC_NAME"),
      align: "left",
      width: "auto",
      type: "text",
      sort: true,
      search: true,
    },
    {
      id: "actionArrow",
      type: "actionArrow",
      label: "",
      align: "center",
      width: "40px",
    },
  ];
};

//
// --- Hook Implementation ---
//

export const useMyTimesheetWorktimeSummaryData = ({
  periodStart,
  periodEnd,
  period,
  tableData,
  roleFilters,
  role,
}: UseParams): UseReturn => {
  const { t, i18n } = useTranslation();
  const [queryData, setQueryData] = useState<QueryResponse>({
    page: 1,
    pageSize: 10,
    rows: [],
    total: 0,
    totalPages: 0,
  });

  const [runQuery, { loading, error }] = useMutation<
    MutationResponse,
    MutationVariables
  >(MY_TIMESHEET_GET_GROUPED_BY_USERS_DASHBOARD_MONTHLY_LIST as DocumentNode, {
    variables: {
      page: tableData?.page,
      pageSize: tableData?.pageSize,
      startTime: periodStart!,
      endTime: periodEnd!,
      roleFilters: roleFilters as RoleFilterInput,
      sortField: tableData?.sortField,
      sortDirection: tableData?.sortDirection,
      searchField: tableData?.searchField,
      searchText: tableData?.searchText,
      role,
    },
    onCompleted(data) {
      setQueryData(data.groupedByUsersDashboardMonthlyList);
    },
    onError(err) {
      console.error("Mutation error:", err);
    },
  });

  useEffect(() => {
    if (periodStart && periodEnd && period === "month") {
      runQuery();
    }
  }, [periodStart, periodEnd, period, runQuery]);

  const rawRows = queryData.rows;
  const weeks = rawRows[0]?.weeks ?? [];

  const columnDefsMobile = {
    id: "my-time-sheet-my-requests-dashboard-table-mobile",
    columns: generateColumnDefsIsMobile(weeks, t),
  };

  const columnDefsDesktop = {
    id: "my-time-sheet-my-requests-dashboard-table-desktop",
    columns: generateColumnDefs(weeks, t),
  };

  const data = rawRows.map((item) => {
    let sumDuration = "00:00";
    const weekDurations = item.weeks?.map((week) => {
      sumDuration = sumTimeStrings([sumDuration, week.duration]);
      return week.duration;
    }) as string[];

    return {
      _id: item.contractId,
      employeeName: parsePipedName(item.employeeName, i18n),
      employeeCode: item.employeeCode,
      ...weekDurations.reduce<Record<string, string>>((acc, dur, idx) => {
        acc[String(idx)] = dur;
        return acc;
      }, {}),
      sumDuration,
    };
  });

  return {
    columnDefsMobile,
    columnDefsDesktop,
    data,
    loading,
    error,
    total: queryData.total,
    totalPages: queryData.totalPages,
    page: queryData.page,
    pageSize: queryData.pageSize,
    runQuery,
    queryData,
  };
};

export default useMyTimesheetWorktimeSummaryData;
